import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Suspense, useMemo, useState } from 'react';

import { Btn } from '../../components/Btn';
import { Card } from '../../components/Card';
import { LineTrend } from '../../components/charts';
import { ExportMenu } from '../../components/ExportMenu';
import { KpiCard } from '../../components/KpiCard';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/Table';
import type {
  MonthlyBranchSales,
  MonthlyCategorySales,
  MonthlyProductSales,
} from '../../data/fetchers';
import { getMonthlySales, getMonthlySalesDetailed } from '../../data/fetchers';
import { CHART_H } from '../../lib/chartTokens';
import type { ExportCol } from '../../lib/export';
import {
  fmtAmount,
  fmtBaht,
  fmtBahtShort,
  fmtPct,
  fmtSatangCompact,
} from '../../lib/format';
import { useBranchScope } from '../../lib/useBranchScope';

const deltaFrac = (cur: number, prev: number): number =>
  prev > 0 ? (cur - prev) / prev : cur > 0 ? 1 : 0;

interface MonthRow {
  month: string;
  revenue: number;
  mom: number | null;
  share: number;
}
interface MonthExportRow {
  month: string;
  revenueBaht: string;
  revenueSatang: number;
  momPct: string;
  sharePct: string;
}
interface MonthlyProductExportRow {
  productId: string;
  name: string;
  qty: number;
  amountBaht: string;
  amountSatang: number;
}

const Delta = ({ v }: { v: number | null }) => {
  if (v == null) return <span className="text-ink-4">—</span>;
  const cls = v > 0.001 ? 'text-ok' : v < -0.001 ? 'text-danger' : 'text-ink-4';
  return (
    <span className={`num font-bold ${cls}`}>
      {v > 0 ? '▲ +' : v < 0 ? '▼ ' : '– '}
      {Math.abs(v * 100).toFixed(1)}%
    </span>
  );
};

const exportCols: ExportCol<MonthExportRow>[] = [
  { key: 'month', header: 'เดือน' },
  { key: 'revenueBaht', header: 'ยอดขาย' },
  { key: 'revenueSatang', header: 'ยอดขาย (satang)' },
  { key: 'momPct', header: 'Δ MoM' },
  { key: 'sharePct', header: 'สัดส่วนทั้งปี' },
];

const detailedProductExportCols: ExportCol<MonthlyProductExportRow>[] = [
  { key: 'productId', header: 'รหัสสินค้า' },
  { key: 'name', header: 'ชื่อสินค้า' },
  { key: 'qty', header: 'จำนวนที่ขาย' },
  { key: 'amountBaht', header: 'ยอดขายรวม' },
  { key: 'amountSatang', header: 'ยอดขายรวม (satang)' },
];

const branchColumns: ColumnDef<
  MonthlyBranchSales & { share: number },
  unknown
>[] = [
  { accessorKey: 'branchName', header: 'สาขา' },
  {
    accessorKey: 'revenue',
    header: 'ยอดขาย',
    cell: (c) => <span className="num">{fmtAmount(c.getValue<number>())}</span>,
  },
  {
    accessorKey: 'share',
    header: 'สัดส่วน',
    cell: (c) => (
      <span className="num">{fmtPct(c.getValue<number>() * 100)}</span>
    ),
  },
  {
    accessorKey: 'mom',
    header: 'Δ MoM',
    cell: (c) => <Delta v={c.getValue<number | null>()} />,
  },
];

const categoryColumns: ColumnDef<
  MonthlyCategorySales & { share: number },
  unknown
>[] = [
  { accessorKey: 'categoryName', header: 'หมวดหมู่' },
  {
    accessorKey: 'amount',
    header: 'ยอดขาย',
    cell: (c) => <span className="num">{fmtAmount(c.getValue<number>())}</span>,
  },
  {
    accessorKey: 'share',
    header: 'สัดส่วน',
    cell: (c) => (
      <span className="num">{fmtPct(c.getValue<number>() * 100)}</span>
    ),
  },
];

const productColumns: ColumnDef<MonthlyProductSales, unknown>[] = [
  { accessorKey: 'name', header: 'ชื่อสินค้า' },
  {
    accessorKey: 'qty',
    header: 'จำนวนที่ขาย',
    cell: (c) => (
      <span className="num">{c.getValue<number>().toLocaleString()}</span>
    ),
  },
  {
    accessorKey: 'amount',
    header: 'ยอดขายรวม',
    cell: (c) => <span className="num">{fmtAmount(c.getValue<number>())}</span>,
  },
];

export function MonthlySalesView() {
  const { scope, ready } = useBranchScope();
  const [requestedMonth, setRequestedMonth] = useState<string>('');

  const { data, isLoading } = useQuery({
    queryKey: ['report', 'monthly', scope.branchId, scope.allowedBranchIds],
    queryFn: () => getMonthlySales(scope),
    enabled: ready,
  });

  const series = data?.series ?? [];
  const labels = data?.labels ?? [];
  const selectedMonth = labels.includes(requestedMonth)
    ? requestedMonth
    : (labels.at(-1) ?? '');

  const { data: detailedData, isLoading: isDetailLoading } = useQuery({
    queryKey: [
      'report',
      'monthly-detail',
      selectedMonth,
      scope.branchId,
      scope.allowedBranchIds,
    ],
    queryFn: () => getMonthlySalesDetailed(selectedMonth, scope),
    enabled: ready && !!selectedMonth,
  });

  const chartData = useMemo(
    () => labels.map((label, i) => ({ label, value: series[i] ?? 0 })),
    [labels, series],
  );

  const rows = useMemo<MonthRow[]>(
    () =>
      labels.map((month, i) => ({
        month,
        revenue: series[i] ?? 0,
        mom: i > 0 ? deltaFrac(series[i], series[i - 1]) : null,
        share: data?.gross ? (series[i] ?? 0) / data.gross : 0,
      })),
    [labels, series, data],
  );
  const exportRows = useMemo<MonthExportRow[]>(
    () =>
      rows.map((r) => ({
        month: r.month,
        revenueBaht: fmtBaht(r.revenue),
        revenueSatang: r.revenue,
        momPct: r.mom == null ? '—' : fmtPct(r.mom * 100),
        sharePct: fmtPct(r.share * 100),
      })),
    [rows],
  );

  const columns = useMemo<ColumnDef<MonthRow, unknown>[]>(
    () => [
      { accessorKey: 'month', header: 'เดือน' },
      {
        accessorKey: 'revenue',
        header: 'ยอดขาย',
        cell: (c) => (
          <span className="num">{fmtAmount(c.getValue<number>())}</span>
        ),
      },
      {
        accessorKey: 'mom',
        header: 'Δ MoM',
        cell: (c) => <Delta v={c.getValue<number | null>()} />,
      },
      {
        accessorKey: 'share',
        header: 'สัดส่วนทั้งปี',
        cell: (c) => (
          <span className="num">{fmtPct(c.getValue<number>() * 100)}</span>
        ),
      },
      {
        id: 'actions',
        header: 'การจัดการ',
        cell: (c) => {
          const m = c.row.original.month;
          const active = m === selectedMonth;
          return (
            <Btn
              kind={active ? 'primary' : 'default'}
              size="sm"
              onClick={() => setRequestedMonth(m)}
            >
              {active ? 'กำลังดูรายละเอียด' : 'ดูรายละเอียด'}
            </Btn>
          );
        },
      },
    ],
    [selectedMonth],
  );

  const branchRows = useMemo(() => {
    if (!detailedData?.branches) return [];
    const total = detailedData.gross || 1;
    return detailedData.branches.map((b) => ({
      ...b,
      share: b.revenue / total,
    }));
  }, [detailedData]);

  const categoryRows = useMemo(() => {
    if (!detailedData?.categories) return [];
    const total = detailedData.grossBeforeDiscount || 1;
    return detailedData.categories.map((c) => ({
      ...c,
      share: c.amount / total,
    }));
  }, [detailedData]);

  const currentMonth = series.length ? series[series.length - 1] : 0;
  const momCurrent =
    series.length > 1
      ? deltaFrac(currentMonth, series[series.length - 2])
      : undefined;
  const ytd = data?.gross ?? 0;
  const avgMonth = series.length ? ytd / series.length : 0;
  const forecast = data?.forecast ?? 0;
  const bestIdx = series.length ? series.indexOf(Math.max(...series)) : -1;

  return (
    <div className="bo-fade space-y-6">
      <PageHeader
        title="ยอดขายรายเดือน"
        subtitle="แนวโน้มรายเดือน · การเติบโต (MoM) · สัดส่วน และการคาดการณ์"
        actions={
          <ExportMenu
            rows={exportRows}
            cols={exportCols}
            filename="ยอดขายรายเดือน"
            title="ยอดขายรายเดือน"
            pdf
          />
        }
      />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard
          label="เดือนปัจจุบัน"
          value={fmtBaht(currentMonth)}
          tone="primary"
          delta={momCurrent}
          sub="เทียบเดือนก่อน (MoM)"
        />
        <KpiCard
          label="สะสมทั้งปี (YTD)"
          value={fmtBaht(ytd)}
          tone="accent"
          sub={`เฉลี่ย ${fmtBahtShort(avgMonth)}/เดือน`}
        />
        <KpiCard
          label="คาดการณ์เดือนหน้า"
          value={fmtBaht(forecast)}
          tone="ok"
          sub="ค่าเฉลี่ย 3 เดือนล่าสุด"
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <h3 className="font-bold text-sm-label text-ink-3 uppercase tracking-wide">
            กำไรขั้นต้น (ตามต้นทุนจริง)
          </h3>
        </div>
        <p className="text-sm text-ink-3">
          ยอดสินค้าเก็บก่อนส่วนลดระดับบิล
          ส่วนโปรโมชั่นและของแถมแยกไว้สำหรับงานบัญชี
        </p>
        {(detailedData?.legacyAccountingReceiptCount ?? 0) > 0 && (
          <p className="rounded-btn bg-warn-light px-3 py-2 text-sm text-warn">
            มีข้อมูลเก่า{' '}
            {detailedData!.legacyAccountingReceiptCount.toLocaleString()}{' '}
            บิลที่ยังไม่มีการแยกยอดก่อนส่วนลด ระบบจึงใช้ยอดสุทธิของบิลเหล่านั้น
            และไม่นำโปรโมชั่นเดิมมารวมซ้ำ
          </p>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-7">
          <KpiCard
            label="ยอดก่อนส่วนลด"
            value={
              detailedData ? fmtBaht(detailedData.grossBeforeDiscount) : '—'
            }
            tone="primary"
          />
          <KpiCard
            label="มูลค่าโปรโมชั่น"
            value={detailedData ? fmtBaht(detailedData.promotionDiscount) : '—'}
            tone="warn"
          />
          <KpiCard
            label="ส่วนลดท้ายบิล"
            value={detailedData ? fmtBaht(detailedData.billDiscount) : '—'}
            tone="warn"
          />
          <KpiCard
            label="ยอดปัดเศษ"
            value={detailedData ? fmtBaht(detailedData.roundingDelta) : '—'}
            tone="primary"
          />
          <KpiCard
            label="ต้นทุนขาย (COGS)"
            value={detailedData ? fmtBaht(detailedData.cogs) : '—'}
            tone="warn"
            sub="Σ จำนวนขาย × ต้นทุน ณ เวลาขาย"
          />
          <KpiCard
            label="กำไรขั้นต้น"
            value={detailedData ? fmtBaht(detailedData.grossProfit) : '—'}
            tone="ok"
            sub="ยอดขายสุทธิ − COGS"
          />
          <KpiCard
            label="อัตรากำไรขั้นต้น"
            value={
              detailedData && detailedData.gross > 0
                ? (
                    (detailedData.grossProfit / detailedData.gross) *
                    100
                  ).toFixed(1)
                : '—'
            }
            unit="%"
            tone="primary"
            sub="กำไรขั้นต้น ÷ ยอดขายสุทธิ"
          />
        </div>
      </div>

      <Card title={`สรุปโปรโมชั่น · ${selectedMonth || '—'}`}>
        {(detailedData?.promotions ?? []).length === 0 ? (
          <div className="py-8 text-center text-body text-ink-3">
            ไม่มีโปรโมชั่นในเดือนนี้
          </div>
        ) : (
          <div className="divide-y divide-line">
            {detailedData!.promotions.map((promotion) => (
              <div
                key={promotion.promotionId}
                className="grid gap-2 py-3 sm:grid-cols-[minmax(0,1fr)_auto_auto_auto] sm:items-center"
              >
                <div className="font-medium text-ink">
                  {promotion.promotionName}
                </div>
                <div className="num text-sm text-ink-3">
                  {promotion.receiptCount.toLocaleString()} บิล
                </div>
                <div className="num text-sm text-ink-3">
                  {promotion.freeQuantity > 0
                    ? `แถม ${promotion.freeQuantity.toLocaleString()} ชิ้น`
                    : 'ไม่มีของแถม'}
                </div>
                <div className="num font-bold text-warn">
                  {fmtBaht(promotion.discountAmount)}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card
        title="แนวโน้มรายเดือน"
        actions={
          bestIdx >= 0 ? (
            <span className="text-sm-label text-ink-3">
              เดือนสูงสุด: <b className="text-ink">{labels[bestIdx]}</b> ·{' '}
              {fmtBahtShort(series[bestIdx])}
            </span>
          ) : undefined
        }
      >
        <Suspense
          fallback={
            <div
              className="bo-card animate-pulse"
              style={{ height: CHART_H.md }}
            />
          }
        >
          <LineTrend
            data={chartData}
            valueFormat={fmtBahtShort}
            tickFormat={fmtSatangCompact}
          />
        </Suspense>
      </Card>

      {!ready || isLoading ? (
        <div className="bo-card h-40 animate-pulse" />
      ) : (
        <DataTable columns={columns} data={rows} />
      )}

      {/* Monthly Sales Drilldown Details */}
      <div className="border-t border-line pt-6 space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-ink flex items-center gap-2">
              <span>เจาะลึกยอดขายรายมิติ</span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary-light text-primary">
                ประจำเดือน {selectedMonth}
              </span>
            </h2>
            <p className="text-sm text-ink-3 mt-1">
              โครงสร้างยอดขายตามมิติสาขา หมวดหมู่สินค้า
              และสินค้าที่ขายดีประจำเดือน {selectedMonth}
            </p>
          </div>
          <div className="flex items-center">
            {detailedData && (
              <ExportMenu
                rows={detailedData.products.map((product) => ({
                  productId: product.productId,
                  name: product.name,
                  qty: product.qty,
                  amountBaht: fmtBaht(product.amount),
                  amountSatang: product.amount,
                }))}
                cols={detailedProductExportCols}
                filename={`ยอดขายรายสินค้า - เดือน ${selectedMonth}`}
                title={`รายงานยอดขายสินค้าเจาะลึก - เดือน ${selectedMonth}`}
                pdf
              />
            )}
          </div>
        </div>

        {isDetailLoading || !ready ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bo-card h-48 animate-pulse bg-bg-warm" />
            <div className="bo-card h-48 animate-pulse bg-bg-warm" />
            <div className="bo-card h-48 animate-pulse bg-bg-warm" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
            {/* Branch Breakdown Column */}
            <div className="space-y-2">
              <h3 className="font-bold text-sm-label text-ink-3 uppercase tracking-wide px-1">
                ยอดขายแยกตามสาขา
              </h3>
              <DataTable
                columns={branchColumns}
                data={branchRows}
                pageSize={5}
                emptyMessage="ไม่มีข้อมูลสาขาสำหรับเดือนนี้"
              />
            </div>

            {/* Category Breakdown Column */}
            <div className="space-y-2">
              <h3 className="font-bold text-sm-label text-ink-3 uppercase tracking-wide px-1">
                ยอดขายแยกตามหมวดหมู่
              </h3>
              <DataTable
                columns={categoryColumns}
                data={categoryRows}
                pageSize={5}
                emptyMessage="ไม่มีข้อมูลหมวดหมู่สำหรับเดือนนี้"
              />
            </div>

            {/* Top Products Breakdown Column */}
            <div className="space-y-2">
              <h3 className="font-bold text-sm-label text-ink-3 uppercase tracking-wide px-1">
                สินค้าขายดี 5 อันดับแรก
              </h3>
              <DataTable
                columns={productColumns}
                data={detailedData?.topProducts ?? []}
                pageSize={5}
                emptyMessage="ไม่มีข้อมูลยอดขายรายสินค้า"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
